import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lightbulb, Rocket, Zap, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import Layout from '../components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateInvention = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    problem_solved: '',
    target_market: '',
    unique_value: '',
    budget_range: '',
    timeline_weeks: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description) {
      toast.error('Please fill in at least the name and description');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/inventions-os/create`,
        {
          title: formData.name,
          raw_prompt: formData.description,
          category: formData.problem_solved || '',
          target_customer: formData.target_market || '',
          target_price: formData.budget_range || ''
        },
        { headers: getAuthHeaders() }
      );

      toast.success('Invention created! LEE is generating the spec now...');
      navigate(`/inventions/${response.data.idea_id}`);
    } catch (error) {
      console.error('Error creating invention:', error);
      toast.error(error.response?.data?.detail || 'Failed to create invention');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-[#050505] p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/inventions')}
              className="mb-4 text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Inventions
            </Button>
            
            <div className="flex items-center gap-4 mb-2">
              <div className="p-3 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl">
                <Lightbulb className="w-8 h-8 text-blue-400" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-white">Create New Invention</h1>
                <p className="text-gray-400 mt-1">LEE will autonomously bring your idea to life</p>
              </div>
            </div>
          </div>

          {/* Autonomous Features Banner */}
          <Card className="bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 border-blue-500/20 p-6 mb-8">
            <div className="flex items-start gap-4">
              <Rocket className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Fully Autonomous Mode</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Automatic supplier research & outreach</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">AI-powered RFQ generation</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Autonomous negotiation & communication</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Real-time progress tracking</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Smart decision making with approval gates</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Complete end-to-end execution</span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <p className="text-xs text-yellow-200">
                    <Zap className="w-3 h-3 inline mr-1" />
                    <strong>You only need to:</strong> Provide the idea and approve key decisions. LEE handles everything else.
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <Card className="bg-[#0A0A0A] border-gray-800 p-8">
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Basic Information</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Invention Name <span className="text-red-400">*</span>
                      </label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., On Point Jar - Smart Storage Solution"
                        className="bg-[#050505] border-gray-700 text-white"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Description <span className="text-red-400">*</span>
                      </label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Describe your invention in detail. What is it? How does it work? What materials or features does it have?"
                        className="bg-[#050505] border-gray-700 text-white min-h-[120px]"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        The more detail you provide, the better LEE can source and execute
                      </p>
                    </div>
                  </div>
                </div>

                {/* Market & Value */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Market & Value Proposition</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Problem Solved
                      </label>
                      <Textarea
                        value={formData.problem_solved}
                        onChange={(e) => setFormData({ ...formData, problem_solved: e.target.value })}
                        placeholder="What problem does this invention solve?"
                        className="bg-[#050505] border-gray-700 text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Target Market
                      </label>
                      <Input
                        value={formData.target_market}
                        onChange={(e) => setFormData({ ...formData, target_market: e.target.value })}
                        placeholder="e.g., Home organization enthusiasts, Small business owners"
                        className="bg-[#050505] border-gray-700 text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Unique Value Proposition
                      </label>
                      <Textarea
                        value={formData.unique_value}
                        onChange={(e) => setFormData({ ...formData, unique_value: e.target.value })}
                        placeholder="What makes this invention unique or better than existing solutions?"
                        className="bg-[#050505] border-gray-700 text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Execution Parameters */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Execution Parameters (Optional)</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Budget Range
                      </label>
                      <Input
                        value={formData.budget_range}
                        onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                        placeholder="e.g., $5,000 - $10,000"
                        className="bg-[#050505] border-gray-700 text-white"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        LEE will find the best suppliers within this range
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Timeline (weeks)
                      </label>
                      <Input
                        type="number"
                        value={formData.timeline_weeks}
                        onChange={(e) => setFormData({ ...formData, timeline_weeks: e.target.value })}
                        placeholder="e.g., 12"
                        className="bg-[#050505] border-gray-700 text-white"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Target timeline for bringing invention to life
                      </p>
                    </div>
                  </div>
                </div>

                {/* Submit */}
                <div className="flex gap-4 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/inventions')}
                    className="flex-1"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Rocket className="w-4 h-4 mr-2" />
                        Launch Autonomous Invention
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default CreateInvention;
